import React, { PropsWithChildren, ReactElement } from "react";
import styled from "@emotion/styled";
import * as Accordion from "@radix-ui/react-accordion";
import { CHEVRON_DOWN_ICON_THIN } from "../../../utils/svgs_new";
import { AppIcon } from "../AppIcon";
import { IsDefined } from "../IsDefined";
import { AccordionChild } from "./AppAccordion.types";
import Flex from "../Flex";
import { keyframes } from "@emotion/react";

interface Props {
  children: AccordionChild[];
}

export const AppAccordion = (props: PropsWithChildren<Props>): ReactElement => {
  return (
    <Accordion.Root type="single" collapsible>
      {props.children.map((child, index) => (
        <AccordionItem value={"accordion-item-" + index} key={"accordion-item-" + index}>
          <StyledAccordionTrigger trailing={child.headerTrailing}>
            {child.header}
          </StyledAccordionTrigger>
          <StyledAccordionContent>{child.items}</StyledAccordionContent>
        </AccordionItem>
      ))}
    </Accordion.Root>
  );
};

interface AccordionTriggerWithTrailingProps extends React.ComponentProps<typeof Accordion.Trigger> {
  trailing?: React.ReactNode;
}

const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerWithTrailingProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <AccordionHeader>
      <Accordion.Trigger className={className} {...props} ref={forwardedRef}>
        <AccordionTriggerFlex justifyContent="space-between">
          {children}
          <Flex gap={8}>
            <IsDefined value={props.trailing}>
              {(trailing) => <div className="trailing">{trailing}</div>}
            </IsDefined>
            <AccordionChevron icon={CHEVRON_DOWN_ICON_THIN} fill={"black"} />
          </Flex>
        </AccordionTriggerFlex>
      </Accordion.Trigger>
    </AccordionHeader>
  )
);

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof Accordion.Content>
>(({ children, className, ...props }, forwardedRef) => (
  <Accordion.Content className={className} {...props} ref={forwardedRef}>
    <AccordionContentText>{children}</AccordionContentText>
  </Accordion.Content>
));

const AccordionHeader = styled(Accordion.Header)`
  display: flex;
  margin: 0;
  margin: 10px 0 0 0;
`;

const slideDown = keyframes`
  from {
    height: 0;
  }
  to {
    height: var(--radix-accordion-content-height);
  }`;

const slideUp = keyframes`
  from {
    height: var(--radix-accordion-content-height);
  }
  to {
    height: 0;
  }`;

const AccordionItem = styled(Accordion.Item)`
  overflow: hidden;

  &:focus-within {
    position: relative;
    z-index: 1;
  }

  &:not(:last-child) ${AccordionHeader} {
    border-bottom: 1px solid #e3e4e5;
  }
`;

const AccordionChevron = styled(AppIcon)`
  transition: transform 300ms cubic-bezier(0.87, 0, 0.13, 1);
`;

const StyledAccordionTrigger = styled(AccordionTrigger)`
  background-color: transparent;
  height: 50px;
  flex: 1;
  display: flex;
  align-items: center;
  font-size: 18px;
  border: 0;

  &:hover {
    background-color: #f3f4f5;
  }

  &[data-state="open"] ${AccordionChevron} {
    transform: rotate(180deg);
  }
`;

const StyledAccordionContent = styled(AccordionContent)`
  overflow: hidden;

  &[data-state="open"] {
    animation: ${slideDown} 300ms cubic-bezier(0.87, 0, 0.13, 1);
  }

  &[data-state="closed"] {
    animation: ${slideUp} 300ms cubic-bezier(0.87, 0, 0.13, 1);
  }
`;

const AccordionContentText = styled.div`
  padding: 0px 10px;
`;

const AccordionTriggerFlex = styled(Flex)`
  width: 100%;
`;

export default AppAccordion;
